import { EnvModel } from '../env/model';
import { action } from 'mobx';
import { AuthModel } from '../auth/model';
import { StorageModel } from '../storage/model';
import { RequestModel } from './model';
import { APIError } from '../api/types';

export class RequestController {
  constructor(
    private env: EnvModel,
    private auth: AuthModel,
    private storage: StorageModel,
    private model: RequestModel,
  ) {}

  url = (path: string) => (path[0] === '/' ? this.env.apiUri + path : path);

  @action
  get = async (options: RequestOptions) => this.fetch('GET', options);

  @action
  patch = async (options: RequestOptions) => this.fetch('PATCH', options);

  @action
  post = async (options: RequestOptions) => this.fetch('POST', options);

  @action
  del = async (options: RequestOptions) => this.fetch('DELETE', options);

  private fetch = async (method: string, options: RequestOptions) => {
    let permitted = true;
    let res;

    if (this.model.refresh) {
      permitted = await this.model.refresh;
    } else if (this.tokenExpiresSoon()) {
      this.model.refresh = this.refresh();
      permitted = await this.model.refresh;
    }

    if (!permitted) {
      this.auth.mustReAuth = true;
    }

    const request = () =>
      fetch(this.url(options.url), this.options({ ...options, mode: options.mode }, method)).catch((err) => {
        // Fetch API throws a TypeError for network problems, wrap it in an APIError so it's easy to differentiate
        let message = 'An unknown error occurred';
        if (err instanceof Error) {
          message = err.message;
        }

        throw new APIError(-1, `An error occurred while fetching: ${message}`);
      });

    res = await request();

    if (res.status === 401) {
      this.model.refresh = this.refresh();
      permitted = await this.model.refresh;

      if (permitted) {
        res = await request();
      } else {
        this.auth.mustReAuth = true;
      }
    }

    return res;
  };

  private refresh = async () => {
    if (!this.auth.token) return false;
    const url = this.env.baseUrl + '/auth/refresh';
    const req = fetch(url, this.options({}, 'get'));

    const res = await req;

    const auth = res.headers.get('Authorization');
    if (!auth) return false;

    const token = auth.match(RequestController.BearerRegExp)[1];
    if (!token) return false;

    this.storage.setItem('token', token);

    return res.status >= 200 && res.status < 400;
  };

  private options = (options: RequestOptions, method: string): RequestOptions & RequestInit => ({
    ...options,
    method,
    headers: {
      ...(options.headers ? options.headers : {}),
      ...(this.auth.token ? { Authorization: `Bearer ${this.auth.token}` } : {}),
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    mode: 'cors' as RequestMode,
  });

  private tokenExpiresSoon = () => {
    if (!this.auth.tokenDecoded) return false;

    const secondsRemaining = this.auth.tokenDecoded.exp - Date.now() / 1000;
    return secondsRemaining < RequestController.RefreshWithin;
  };

  static BearerRegExp = /Bearer (.+)/;

  static RefreshWithin = 5 * 60;
}

export interface RequestOptions {
  url?: string;
  body?: any;
  headers?: { [key: string]: string };
  mode?: RequestMode;
}
